import { useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, RefreshCw } from "lucide-react";

const abs = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const OK = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

// Secure image upload with preview / replace / remove. Uploads to a backend endpoint that
// validates + re-encodes the file; the caller receives a public URL via onChange.
export default function ImageUpload({ value, onChange, label = "Photo / logo", endpoint = "/vendors/upload-image", testid = "image-upload" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type && !OK.includes(file.type)) {
      toast.error("Ce format de fichier n'est pas accepté. Utilisez une image JPG, PNG ou WebP.");
      if (ref.current) ref.current.value = ""; return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Cette image dépasse la taille maximale autorisée de 10 Mo.");
      if (ref.current) ref.current.value = ""; return;
    }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(abs(data.url));
      toast.success("Image ajoutée");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Cette image n'a pas pu être lue. Veuillez en choisir une autre.");
    } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  return (
    <div data-testid={testid}>
      {label && <div className="text-sm text-stone-600 mb-1.5">{label}</div>}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      {value ? (
        <div className="flex items-center gap-3">
          <img src={abs(value)} alt="Aperçu" loading="lazy" className="h-24 w-24 rounded-xl border object-cover shrink-0" data-testid={`${testid}-preview`} />
          <div className="flex flex-col gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-replace`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Remplacer
            </Button>
            <Button type="button" variant="outline" size="sm" className="rounded-full text-destructive" onClick={() => onChange("")} data-testid={`${testid}-remove`}>
              <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer
            </Button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl py-8 text-stone-500 hover:border-stone-400 hover:text-stone-700 transition-colors"
          data-testid={`${testid}-btn`}>
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
          <span className="text-sm font-medium">Choisir une image</span>
          <span className="text-xs text-stone-400">JPG, PNG ou WebP · 10 Mo max</span>
        </button>
      )}
    </div>
  );
}
